import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Input,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  AuthStore,
  ActiveErrorsStore,
  DevicesStore,
  FeatureService,
  InstallationDetailsStore,
  RemoteServiceDiagnosisChartLoadingStatus,
  RemoteServiceDiagnosisEntry,
  RemoteServiceDiagnosisLoadingStatus,
  RemoteServiceDiagnosisTag,
  RemoteServiceStore,
  UnitsStore,
  RemoteServicePriority,
} from '@store';

import { DiagnosisExplanationModalComponent } from '../diagnosis-explanation-modal/diagnosis-explanation-modal.component';

import moment from 'moment';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, filter, switchMap, take, takeUntil } from 'rxjs/operators';

import { RemoteServiceService } from '../../remote-service.service';
import { DateRangeForm } from '../../../../../shared/common/form-types';
import { CustomTranslationsService } from '../../../../../operational-data-common/translations';
import { TranslateService } from '@ngx-translate/core';
import { ElementRef } from '@angular/core';
import { TextParserService } from 'src/app/shared/services/text-parser-service.service';
import { concatLatestFrom } from '@ngrx/effects';

@Component({
  selector: 'app-remote-service-diagnosis',
  templateUrl: './remote-service-diagnosis.component.html',
  styleUrls: ['./remote-service-diagnosis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteServiceDiagnosisComponent implements OnInit, OnDestroy {
  @Input() diagnosisList: RemoteServiceDiagnosisEntry[];
  @Input() type: 'diagnosis' | 'anomaly' = 'diagnosis';

  readonly highlightClassName = 'highlight';
  // hex values taken from variables.scss
  readonly infoHighlightStyling = {
    backgroundColor: '#00a4ba',
    padding: '0 8px',
    color: '#ffffff',
  } as CSSStyleDeclaration;
  readonly warningHighlightStyling = {
    backgroundColor: '#fdd766',
    padding: '0 8px',
  } as CSSStyleDeclaration;
  readonly remoteServicePriority = RemoteServicePriority;

  RemoteServiceDiagnosisLoadingStatus = RemoteServiceDiagnosisLoadingStatus;
  RemoteServiceDiagnosisChartLoadingStatus = RemoteServiceDiagnosisChartLoadingStatus;
  MAX_COLLAPSED_ENTRIES = 8;
  diagnosisCalculationDataStatus: RemoteServiceDiagnosisLoadingStatus;

  dateRange: FormGroup<DateRangeForm> = new FormGroup({
    start: new FormControl<Date | null>(null, [Validators.required]),
    end: new FormControl<Date | null>(null, [Validators.required]),
  });
  minRangepickerDate: Date;
  maxRangepickerDate: Date;
  customDateRange = false;
  isEntrySelected = false;
  thresholdsExpanded = false;
  gatewayRegistrationDate: Date;
  canDisplayAllEntries = false;
  textContent: { type: string; content: string }[] = [];
  isE3HeatpumpDevice$: Observable<boolean>;
  isRsa$ = this.authStore.isRsaUser();

  @ViewChild('thresholdSettings', { static: false }) thresholdSettings: ElementRef;
  deviceSerial: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public customTranslationsService: CustomTranslationsService,
    public remoteServiceService: RemoteServiceService,
    public remoteServiceStore: RemoteServiceStore,
    public unitsStore: UnitsStore,
    public installationDetailsStore: InstallationDetailsStore,
    public deviceStore: DevicesStore,
    public activeErrorsStore: ActiveErrorsStore,
    private translation: TranslateService,
    private cdr: ChangeDetectorRef,
    private featureService: FeatureService,
    private activatedRoute: ActivatedRoute,
    private textParserService: TextParserService,
    private authStore: AuthStore,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const textTranslation = this.translation.instant('+REMOTE_SERVICE.GUIDANCE.TEXT');
    const keyTranslation = this.translation.instant('+REMOTE_SERVICE.GUIDANCE.KEY_TRANSLATION');
    this.textContent = this.textParserService.parseTextContent(textTranslation, keyTranslation);
    this.setInitialValues();
    this.setDelayOnSelectedEntryChanged();

    this.remoteServiceStore.diagnosisCalculationDataStatus$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((dataStatus) => {
        this.diagnosisCalculationDataStatus = dataStatus;
      });

    this.activatedRoute.queryParamMap
      .pipe(
        concatLatestFrom(() => this.authStore.isRsaUser()),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([queryParam, isRSAUser]) => {
        this.deviceSerial = queryParam.get('deviceSerial');
        if (this.deviceSerial) {
          this.remoteServiceStore.loadChartsFeatures(this.deviceSerial);
          if (isRSAUser) {
            this.loadExplainableDiagnosis();
          }
        }
      });

    this.remoteServiceStore.remoteServiceDiagnosisLoadingStatus$
      .pipe(
        filter((status) => status === RemoteServiceDiagnosisLoadingStatus.DataPresent),
        switchMap(() => {
          return this.type === 'diagnosis'
            ? this.remoteServiceStore.diagnosisList$.pipe(
                filter((entry) => !!entry?.length),
                take(1)
              )
            : this.remoteServiceStore.anomalyList$.pipe(
                filter((entry) => !!entry?.length),
                take(1)
              );
        })
      )
      .subscribe(() => {
        this.type === 'diagnosis'
          ? this.remoteServiceStore.selectFirstDiagnosisEntry()
          : this.remoteServiceStore.selectFirstAnomalyEntry(false);
      });

    this.isE3HeatpumpDevice$ = this.remoteServiceService.getIsE3HeatpumpDevice();
  }

  scrollToThresholdSettings(): void {
    if (this.thresholdSettings?.nativeElement) {
      this.thresholdSettings.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      this.thresholdsExpanded = true;
    }
  }

  openDiagnosisExplanation(entry: RemoteServiceDiagnosisEntry): void {
    const diagnosisName = entry.diagnosis_name;
    const boilerId = this.deviceSerial;
    const lang = this.translation.currentLang?.split('-')[0] ?? 'en';
    const startDatetime = entry.diagnosis_date;
    const endDatetime = entry.diagnosis_date;

    this.remoteServiceStore.loadDiagnosisExplanation(
      diagnosisName,
      boilerId,
      lang,
      startDatetime,
      endDatetime
    );

    this.dialog.open(DiagnosisExplanationModalComponent, {
      panelClass: 'diagnosis-explanation-dialog',
      width: '600px',
      data: {
        diagnosisName,
        diagnosisTitleKey: this.remoteServiceService.getRemoteServicePrefix(diagnosisName),
      },
    });
  }

  canAddLineBreak(diagnosis: RemoteServiceDiagnosisEntry): boolean {
    const hasAnyChild = this.diagnosisList.some((d) => d.meta_information?.parent_diagnosis);

    if (!hasAnyChild) {
      return false;
    }

    const isParent = !diagnosis.meta_information.parent_diagnosis;

    if (isParent) {
      const hasChildren = this.diagnosisList.some(
        (d) => d.meta_information.parent_diagnosis === diagnosis.diagnosis_id
      );

      return !hasChildren;
    } else {
      return this.isLastChildOfParent(diagnosis);
    }
  }

  isLastChildOfParent(diagnosis: RemoteServiceDiagnosisEntry): boolean {
    const parentId = diagnosis.meta_information.parent_diagnosis;

    if (!parentId || !this.diagnosisList?.length) {
      return false;
    }

    const siblings = this.diagnosisList.filter(
      (d) => d.meta_information.parent_diagnosis === parentId
    );

    return siblings[siblings.length - 1].diagnosis_id === diagnosis.diagnosis_id;
  }

  canDisplayDiagnosisEntry(index: number): boolean {
    return this.canDisplayAllEntries ? true : index < this.MAX_COLLAPSED_ENTRIES;
  }

  isMoreButtonVisible(): boolean {
    const manyEntries = this.diagnosisList?.length > this.MAX_COLLAPSED_ENTRIES;

    return manyEntries;
  }

  showLess(): void {
    this.canDisplayAllEntries = false;
  }

  showMore(): void {
    this.canDisplayAllEntries = true;
  }

  onThresholdsToggle(): void {
    this.thresholdsExpanded = !this.thresholdsExpanded;
  }

  onDiagnosisEntrySelected(event: any, entry: RemoteServiceDiagnosisEntry): void {
    event.preventDefault();
    this.remoteServiceStore.selectDiagnosisEntry(entry);
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  setDayRange(): void {
    const startDate = this.dateRange.value.start;
    const endDate = this.dateRange.value.end;

    if (startDate && endDate) {
      const startPointMs = moment(startDate).utcOffset(0).valueOf();
      const endPointMs = moment(endDate).utcOffset(0).valueOf();

      this.remoteServiceStore.setNewChartExtremes(startPointMs, endPointMs);

      this.customDateRange = true;

      // if we dont set timeout then datepicker panel gets stuck and doesn't hide
      setTimeout(() => {
        this.remoteServiceStore.loadChartsFeatures(this.deviceSerial);
      });
    }
  }

  resetRange(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.setDefaultRange();

    setTimeout(() => {
      this.remoteServiceStore.loadChartsFeatures(this.deviceSerial);
    });
  }

  hasBetaTag(entry: RemoteServiceDiagnosisEntry): boolean {
    return entry?.tags?.includes(RemoteServiceDiagnosisTag.Beta) ?? false;
  }

  hasInternalTag(entry: RemoteServiceDiagnosisEntry): boolean {
    return entry?.tags?.includes(RemoteServiceDiagnosisTag.InternalRsa) ?? false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private loadExplainableDiagnosis(): void {
    this.remoteServiceStore.isDiagnosisWhitelistLoaded$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((loaded) => {
        if (!loaded) {
          this.remoteServiceStore.loadDiagnosisWhitelist(this.deviceSerial);
        }
      });
  }

  private setDefaultRange(): void {
    this.resetDateRangePicker();
    this.cdr.detectChanges();

    const daysBefore30 = moment().subtract(30, 'days').startOf('day').toDate();
    const gatewayRegistrationDateTimeDifference = moment(this.minRangepickerDate).diff(
      daysBefore30,
      'days'
    );
    const isFullRangeNotAvailable = gatewayRegistrationDateTimeDifference >= 0;

    this.dateRange.setValue({
      start: isFullRangeNotAvailable ? this.gatewayRegistrationDate : daysBefore30,
      end: this.maxRangepickerDate,
    });

    this.remoteServiceStore.setNewChartExtremes(
      isFullRangeNotAvailable ? this.gatewayRegistrationDate.getTime() : daysBefore30.getTime(),
      this.maxRangepickerDate.getTime()
    );
    this.dateRange.setErrors({ required: null });

    this.customDateRange = false;
  }

  private resetDateRangePicker(): void {
    this.dateRange.reset();

    Object.keys(this.dateRange.controls).forEach((key) => {
      this.dateRange.get(key).setErrors(null);
    });
  }

  private setDelayOnSelectedEntryChanged(): void {
    this.remoteServiceStore.selectedDiagnosisEntry$
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.isEntrySelected = false;

        setTimeout(() => {
          this.isEntrySelected = true;
          this.cdr.detectChanges();
        });
      });
  }

  private setInitialValues(): void {
    this.featureService
      .selectGatewayProperties()
      .pipe(take(1))
      .subscribe((properties) => {
        this.gatewayRegistrationDate = moment(properties.registeredAt).toDate();

        this.minRangepickerDate = this.gatewayRegistrationDate;
        this.maxRangepickerDate = moment().toDate();

        this.setDefaultRange();
        this.remoteServiceStore.loadChartsConfig();
      });
  }
}
